import { useState } from "react";
import { fileUrl } from "@/lib/api";
import { Eye, MapPin } from "lucide-react";

/**
 * Read-only viewer for annotated comments. Renders a small thumbnail with
 * numbered pins overlaid + hover-to-zoom expansion that overlays the
 * per-pin text bubbles.
 */
const AnnotatedCommentViewer = ({ annotations = [], imageUrl, testid }) => {
  const [expanded, setExpanded] = useState(false);
  if (!annotations?.length || !imageUrl) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setExpanded(true)}
        data-testid={testid || "annotated-comment-thumb"}
        className="mt-2 group relative inline-block rounded-lg overflow-hidden border-2 border-[#06B6D4]/40 hover:border-[#06B6D4] transition-all">
        <img src={fileUrl(imageUrl)} alt="annotated"
          className="block w-44 h-28 object-cover" />
        {annotations.map((p) => (
          <span key={p.label}
            style={{ left: `${p.x}%`, top: `${p.y}%` }}
            className="absolute -translate-x-1/2 -translate-y-1/2 h-4 w-4 rounded-full bg-[#06B6D4] text-white text-[9px] font-bold flex items-center justify-center border border-white shadow">
            {p.label}
          </span>
        ))}
        <span className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
          <span className="opacity-0 group-hover:opacity-100 inline-flex items-center gap-1 text-white text-[11px] bg-black/60 px-2 py-0.5 rounded-full">
            <Eye className="h-3 w-3" /> View pins
          </span>
        </span>
      </button>

      {expanded && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setExpanded(false)}
          data-testid="annotated-viewer-overlay">
          <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[92vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3 border-b border-[#E2E8F0]">
              <p className="text-sm font-semibold inline-flex items-center gap-1.5">
                <MapPin className="h-4 w-4 text-[#06B6D4]" />
                {annotations.length} pin{annotations.length === 1 ? "" : "s"}
              </p>
              <button onClick={() => setExpanded(false)}
                className="text-xs btn-outline px-3 py-1">Close</button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-5 flex-1 overflow-hidden">
              <div className="lg:col-span-3 bg-[#0B1E3F] flex items-center justify-center overflow-auto p-4">
                <div className="relative inline-block">
                  <img src={fileUrl(imageUrl)} alt="annotated full"
                    className="block max-w-full max-h-[75vh] object-contain" />
                  {annotations.map((p) => (
                    <span key={p.label}
                      style={{ left: `${p.x}%`, top: `${p.y}%` }}
                      className="absolute -translate-x-1/2 -translate-y-1/2 h-7 w-7 rounded-full bg-[#06B6D4] text-white text-xs font-bold flex items-center justify-center border-2 border-white shadow-lg">
                      {p.label}
                    </span>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-2 overflow-y-auto p-4 space-y-2">
                {annotations.map((p) => (
                  <div key={p.label} className="rounded-lg border border-[#E2E8F0] p-2.5">
                    <p className="text-xs font-bold text-[#0B1E3F] inline-flex items-center gap-1.5">
                      <span className="h-5 w-5 rounded-full bg-[#06B6D4] text-white text-[10px] flex items-center justify-center">{p.label}</span>
                      Pin {p.label}
                    </p>
                    <p className="text-xs mt-1 whitespace-pre-wrap text-neutral-700">{p.text || <em className="text-neutral-400">No note</em>}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AnnotatedCommentViewer;
